import React, { forwardRef } from 'react';

/**
 * ImagoInput - text field with label, icons, error and helper text.
 *
 * 34px tall (36 at size="lg", which is what the URL bar uses so it matches the
 * Send button beside it). Icon slots reserve 36px of padding and expect a 16px
 * Lucide glyph.
 *
 * Hover and focus both move the border to primary; focus adds the 3px inner
 * ring. That double signal is deliberate - in a dense panel the hover border is
 * often the only affordance that a field is editable.
 *
 * @param {Object} props
 * @param {string} [props.label] - label text displayed above the input
 * @param {string} [props.placeholder] - placeholder text; ends in "..." for prompts
 * @param {string} [props.error] - error message; replaces helperText and sets aria-invalid
 * @param {string} [props.helperText] - helper text displayed below the input
 * @param {React.ReactNode} [props.leftIcon]
 * @param {React.ReactNode} [props.rightIcon]
 * @param {'md' | 'lg'} [props.size='md'] - 34 / 36px tall
 * @param {string} [props.className] - additional CSS classes for the container
 * @param {string} [props.inputClassName] - additional CSS classes for the input element
 * @param {string} [props.id] - input ID (auto-generated if not provided)
 */
export const ImagoInput = forwardRef(function ImagoInput(
  {
    label,
    placeholder,
    error,
    helperText,
    leftIcon,
    rightIcon,
    size = 'md',
    className = '',
    inputClassName = '',
    id,
    type = 'text',
    disabled = false,
    style = {},
    ...rest
  },
  ref
) {
  const generatedId = React.useId();
  const inputId = id || `imago-input-${generatedId}`;

  const baseInputStyles = `
    w-full min-w-0 rounded-md border bg-input text-foreground text-ui
    placeholder:text-muted
    transition-[border-color,box-shadow] duration-base ease-standard
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  // `lg` (the URL bar) steps down to the standard input height below 768px.
  const heightStyles = size === 'lg' ? 'h-input md:h-input-lg' : 'h-input';

  // The border carries the error state, so an errored field drops the focus
  // ring rather than stacking two signals.
  const borderStyles = error
    ? 'border-danger focus:border-danger'
    : 'border-input-border hover:border-primary focus:border-primary focus:shadow-focus-inset';

  const paddingStyles = `
    py-0
    ${leftIcon ? 'pl-9' : 'pl-3'}
    ${rightIcon ? 'pr-9' : 'pr-3'}
  `;

  const combinedInputClassName = `
    ${baseInputStyles}
    ${heightStyles}
    ${borderStyles}
    ${paddingStyles}
    ${inputClassName}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className={`flex flex-col gap-1.5 min-w-0 ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-foreground-secondary"
        >
          {label}
        </label>
      )}

      <div className="relative flex items-center min-w-0">
        {leftIcon && (
          <span className="absolute left-3 flex text-muted pointer-events-none">
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          className={combinedInputClassName}
          style={style}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...rest}
        />

        {rightIcon && (
          <span className="absolute right-3 flex text-muted">
            {rightIcon}
          </span>
        )}
      </div>

      {error && (
        <p id={`${inputId}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={`${inputId}-helper`} className="text-xs text-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});

/**
 * ImagoTextarea - the multi-line sibling.
 *
 * The product styled textareas through the global input rules rather than a
 * component; this exposes the same treatment so a form does not have to reach
 * for raw CSS. Set `mono` for request bodies, scripts and anything else that is
 * code - it switches to IBM Plex Mono at 13px/1.6.
 *
 * @param {Object} props
 * @param {string} [props.label]
 * @param {string} [props.error]
 * @param {string} [props.helperText]
 * @param {number} [props.rows=4]
 * @param {boolean} [props.mono=false] - render as code
 * @param {string} [props.className] - additional CSS classes for the container
 * @param {string} [props.textareaClassName]
 */
export const ImagoTextarea = forwardRef(function ImagoTextarea(
  {
    label,
    error,
    helperText,
    rows = 4,
    mono = false,
    className = '',
    textareaClassName = '',
    id,
    disabled = false,
    style = {},
    ...rest
  },
  ref
) {
  const generatedId = React.useId();
  const textareaId = id || `imago-textarea-${generatedId}`;

  const borderStyles = error
    ? 'border-danger focus:border-danger'
    : 'border-input-border hover:border-primary focus:border-primary focus:shadow-focus-inset';

  const typeStyles = mono
    ? 'font-mono text-ui leading-[1.6]'
    : 'font-sans text-sm';

  const combinedClassName = `
    w-full min-w-0 resize-y rounded-md border bg-input text-foreground
    px-3 py-2
    placeholder:text-muted
    transition-[border-color,box-shadow] duration-base ease-standard
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${typeStyles}
    ${borderStyles}
    ${textareaClassName}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div className={`flex flex-col gap-1.5 min-w-0 ${className}`}>
      {label && (
        <label
          htmlFor={textareaId}
          className="text-xs font-medium text-foreground-secondary"
        >
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        disabled={disabled}
        className={combinedClassName}
        style={style}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
        }
        {...rest}
      />

      {error && (
        <p id={`${textareaId}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={`${textareaId}-helper`} className="text-xs text-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});

export default ImagoInput;
